import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import { emptyArray } from 'ui-components/default-props/empty';

import { messages as ShipmentExportMessages } from './messages/shipment-export-messages';
import { messages as OrderExportMessages } from './messages/order-export-messages';
import { ExportOptionSelectionModalView } from './components/option-selection-modal-view';
import { CustomExportsContainer } from './containers/custom-exports-container';
import { ExportOptions, ExportOptionsValues } from './enums/export-options-enum';
import { DefaultExportFormat } from './enums/default-export-format';
import { ErrorBoundary } from '../../error-boundary';

import style from './export-records-modal.module.less';

const OPTION_SELECTION_VIEW = 'OPTION_SELECTION_VIEW';
const UPDATE_FORMAT_VIEW = 'UPDATE_FORMAT_VIEW';

export const ExportRecordsModal = ({
  closeModal,
  onInitiateExport,
  numberRecordsInView = 0,
  numberRecordsSelected = 0,
  recordExportType,
  exportFormats = emptyArray,
  onDeleteExportFormat,
  onSaveExportFormat,
  onExportTypeChanged,
}) => {
  const [currentView, setView] = useState(OPTION_SELECTION_VIEW);
  const setViewToUpdate = useCallback(() => setView(UPDATE_FORMAT_VIEW), [setView]);
  const [selectedExportFormatTypeId, setSelectedExportFormatTypeId] = useState(DefaultExportFormat.extractId);
  const [primaryRecordsOrLineItems, setPrimaryRecordsOrLineItems] = useState(ExportOptions.EXPORT_PRIMARY_RECORDS);
  const [formatToEdit, setFormatToEdit] = useState({});
  const onEditFormat = useCallback(
    (format) => {
      setFormatToEdit(format);
      setViewToUpdate();
    },
    [setFormatToEdit, setViewToUpdate],
  );

  const setViewToOptionSelection = useCallback(() => {
    setView(OPTION_SELECTION_VIEW);
    setFormatToEdit({});
  }, [setView, setFormatToEdit]);

  const onFormatTypeChanged = useCallback(
    (optionSelected) => {
      setSelectedExportFormatTypeId(optionSelected);
    },
    [setSelectedExportFormatTypeId],
  );

  const onExportRecordTypeChanged = useCallback(
    (optionSelected) => {
      setPrimaryRecordsOrLineItems(optionSelected);
      onExportTypeChanged(optionSelected);
    },
    [setPrimaryRecordsOrLineItems, onExportTypeChanged],
  );

  const messages = recordExportType === ExportOptions.SHIPMENT_RECORD_EXPORT ? ShipmentExportMessages : OrderExportMessages;
  return (
    <Modal className={style['export-records-modal']}>
      <ErrorBoundary errorMessageId='exportRecordsModal'>
        <ModalHeader closeModal={closeModal}>
          <FormattedMessage {...messages.modalTitle} />
        </ModalHeader>
        {currentView === OPTION_SELECTION_VIEW && (
          <ExportOptionSelectionModalView
            closeModal={closeModal}
            exportFormats={exportFormats}
            messages={messages}
            numberRecordsInView={numberRecordsInView}
            numberRecordsSelected={numberRecordsSelected}
            onDeleteExportFormat={onDeleteExportFormat}
            onEditFormat={onEditFormat}
            onExportRecordTypeChanged={onExportRecordTypeChanged}
            onFormatTypeChanged={onFormatTypeChanged}
            onInitiateExport={onInitiateExport}
            primaryRecordsOrLineItems={primaryRecordsOrLineItems}
            recordExportType={recordExportType}
            selectedExportFormatTypeId={selectedExportFormatTypeId}
            setViewToUpdate={setViewToUpdate}
          />
        )}
        {currentView === UPDATE_FORMAT_VIEW && (
          <CustomExportsContainer
            closeModal={closeModal}
            formatId={formatToEdit.extractId}
            formatName={formatToEdit.name}
            onGoBack={setViewToOptionSelection}
            onSaveExportFormat={onSaveExportFormat}
            primaryRecordsOrLineItems={primaryRecordsOrLineItems}
            recordExportType={recordExportType}
          />
        )}
      </ErrorBoundary>
    </Modal>
  );
};

ExportRecordsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  numberRecordsInView: PropTypes.number,
  numberRecordsSelected: PropTypes.number,
  onInitiateExport: PropTypes.func.isRequired,
  onExportTypeChanged: PropTypes.func.isRequired,
  onSaveExportFormat: PropTypes.func.isRequired,
  onDeleteExportFormat: PropTypes.func.isRequired,
  recordExportType: PropTypes.oneOf(ExportOptionsValues).isRequired,
  exportFormats: PropTypes.array,
};
